import { Box, Stack, Typography } from "@mui/material";
import { memo, useMemo } from "react";

import type { ScoutConversationPrivateDocumentRow } from "../../hooks/useScoutConversationPrivateDocuments";
import type { ScoutChatMessage, ScoutFilePart, ScoutTextPart } from "./scoutChatMessage";

import {
  isScoutPrivateDocumentExtractionWatchPartUrl,
  parseScoutExtractionWatchDocumentIdsFromText,
  stripScoutExtractionWatchMarkerFromText,
} from "../scoutExtractionWatchMessage";
import { ScoutLargeDocumentExtractionProgressCard } from "../ScoutLargeDocumentExtractionProgressCard";
import { ScoutFileDisplay } from "./ScoutFileDisplay";

function resolveExtractionWatchDocumentIds(message: ScoutChatMessage): string[] {
  const documentIds = new Set<string>();

  for (const part of message.parts) {
    if (part.type === "file" && isScoutPrivateDocumentExtractionWatchPartUrl(part.url)) {
      const documentId = part.document?.id;
      if (documentId) {
        documentIds.add(documentId);
      }
      const urlId = part.url?.replace("encamp-private-document-extraction://", "");
      if (urlId) {
        documentIds.add(urlId);
      }
    }
  }

  for (const part of message.parts) {
    if (part.type === "text") {
      const partDocumentIds = parseScoutExtractionWatchDocumentIdsFromText(part.text);
      for (const documentId of partDocumentIds) {
        documentIds.add(documentId);
      }
    }
  }

  return [...documentIds];
}

type UserMessageProps = {
  message: ScoutChatMessage;
  tenantId?: string;
  conversationId?: string;
  privateDocuments?: ScoutConversationPrivateDocumentRow[];
};

export const UserMessage = memo(function UserMessage({
  message,
  tenantId,
  conversationId,
  privateDocuments = [],
}: UserMessageProps) {
  const extractionWatchDocumentIds = useMemo(
    () => resolveExtractionWatchDocumentIds(message),
    [message]
  );

  const isAbort = message.parts.some((part) => part.type === "abort");
  if (isAbort) {
    return (
      <Box
        data-testid="scout-message"
        data-scout-message-id={message.id}
        data-scout-message-role="user"
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 1,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textAlign: "center",
            letterSpacing: 0.2,
            userSelect: "none",
          }}
        >
          User cancelled the response.
        </Typography>
      </Box>
    );
  }

  const textParts = message.parts.filter((part): part is ScoutTextPart => part.type === "text");
  const fileParts = message.parts.filter(
    (part): part is ScoutFilePart =>
      part.type === "file" && !isScoutPrivateDocumentExtractionWatchPartUrl(part.url)
  );

  const displayTextParts = textParts
    .map((part) => stripScoutExtractionWatchMarkerFromText(part.text))
    .filter((text) => text.length > 0);

  return (
    <Stack
      data-testid="scout-message"
      data-scout-message-id={message.id}
      data-scout-message-role="user"
      gap={1}
      sx={{
        display: "flex",
        flexDirection: "column",
        py: 1.75,
        px: { xs: 2, sm: 3 },
      }}
    >
      {(displayTextParts.length > 0 || fileParts.length > 0) && (
        <Stack
          gap={1}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          {fileParts.length > 0 ? (
            <Stack spacing={1} useFlexGap flexWrap="wrap">
              {fileParts.map((part) => (
                <ScoutFileDisplay key={part.id} part={part} />
              ))}
            </Stack>
          ) : null}
          {displayTextParts.length > 0 ? (
            <Box
              sx={{
                maxWidth: "min(600px, 100%)",
                px: 2,
                py: 1.5,
                borderRadius: 3,
                bgcolor: "#7092CF",
                color: "white",
                overflowWrap: "break-word",
              }}
            >
              {displayTextParts.map((text, index) => (
                <Typography
                  key={`${message.id}-text-${index}`}
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.58,
                    "&:not(:last-of-type)": { mb: 1 },
                  }}
                >
                  {text}
                </Typography>
              ))}
            </Box>
          ) : null}
        </Stack>
      )}

      {extractionWatchDocumentIds.map((privateDocumentId) => (
        <ScoutLargeDocumentExtractionProgressCard
          key={`${message.id}-extraction-${privateDocumentId}`}
          tenantId={tenantId}
          conversationId={conversationId}
          privateDocumentId={privateDocumentId}
          documents={privateDocuments}
        />
      ))}
    </Stack>
  );
});
UserMessage.displayName = "UserMessage";
